/* v8 ignore start */
<script>
import { CdrText, CdrButton, CdrLink } from '@rei/cedar';
import LandingImageComponent from '@rei/landing-image-component';
import LandingBadge from '@rei/landing-badge';
import { palettes } from '@rei/landing-palettes';
import SecondaryHeading from '../../../../common/components/secondary-heading/SecondaryHeading.vue';

export default {
  name: 'LeadRailCard',
  components: {
    LandingImageComponent,
    LandingBadge,
    SecondaryHeading,
    CdrText,
    CdrButton,
    CdrLink,
  },
  props: {
    content: { type: Object, required: false, default: () => {} },
    visualConfiguration: { type: Object, required: false, default: () => {} },
    highPriority: { type: Boolean, required: false, default: false },
  },
  data() {
    const paletteData = { ...this.visualConfiguration?.palette };
    return {
      palettes: palettes(paletteData),
    };
  },
  computed: {
    cssVars() {
      return {
        ...this.palettes.cssContentPaletteVars(),
        ...this.palettes.cssSurfacePaletteVars(),
      };
    },
    computedActions() {
      const paletteModifiers = this.palettes.paletteCtaModifiers();
      const { primaryCtaModifier, secondaryCtaModifier } = paletteModifiers;
      const allSecondaryButtons = this.visualConfiguration?.secondaryButtonsOnly;
      const computedDefaultActions = this.content.actions.defaultActions.map((action, idx) => ({
        ...action,
        modifier: idx === 0 && !allSecondaryButtons ? primaryCtaModifier : secondaryCtaModifier,
      }));
      return computedDefaultActions;
    },
    effectGradient() {
      const { bannerGradientMode: mode, bannerGradient: gradient } = this.visualConfiguration;
      let backgroundValue = '';
      const darkMode = { base: '#000000', target: 'rgba(0, 0, 0, 0)' };
      const lightMode = { base: '#FFFFFF', target: 'rgba(255, 255, 255, 0)' };
      const modeColor = mode === 'Dark' ? darkMode : lightMode;
      switch (gradient?.xs) {
        case 'to bottom':
          backgroundValue = `linear-gradient(to bottom, ${modeColor.base} 0%, ${modeColor.target} 40%)`;
          break;
        case 'to top':
          backgroundValue = `linear-gradient(to top, ${modeColor.base} 0%, ${modeColor.target} 40%)`;
          break;
        case 'to left':
          backgroundValue = `linear-gradient(to left, ${modeColor.base} 0%, ${modeColor.target} 40%)`;
          break;
        case 'to right':
          backgroundValue = `linear-gradient(to right, ${modeColor.base} 0%, ${modeColor.target} 40%)`;
          break;
        default:
          backgroundValue = '';
      }
      return `--efectGradient: ${backgroundValue}`;
    },
    fetchPriorityValue() {
      return this.highPriority ? 'high' : 'low';
    },
    lazyLoadValue() {
      return !this.highPriority;
    },
  },
};
</script>
<template>
  <div
    ref="card"
    class="lead-rail-card"
    :style="cssVars"
  >
    <div
      class="lead-rail-card__hero"
      :style="effectGradient"
    >
      <landing-image-component
        v-if="content.media"
        v-bind="content.media"
        :lazy-load="lazyLoadValue"
        :fetch-priority="fetchPriorityValue"
        data-ui="lead-component-image"
      />
      <landing-badge
        v-if="content.badge?.text"
        v-bind="content.badge"
        :theme="visualConfiguration?.palette.content"
        class="lead-rail-card__badge"
      />
      <div class="lead-rail-card__hero__heading">
        <landing-image-component
          v-if="content.lockup"
          v-bind="content.lockup"
          class="lead-rail-card__lockup"
          object-fit="cover"
          :lazy-load="lazyLoadValue"
          :fetch-priority="fetchPriorityValue"
          data-ui="lead-component-lockup"
        />
        <secondary-heading
          class="lead-rail-card__hero__title"
          :text="content.heading.text"
        />
      </div>
    </div>
    <div
      class="lead-rail-card__content"
      :style="cssVars"
    >
      <cdr-text
        v-if="content.body"
        data-ui="lead-rail-card-component-body"
        class="lead-rail-card__content__body"
      >
        {{ content.body }}
      </cdr-text>
      <div class="lead-rail-card__content__actions-container">
        <div class="lead-rail-card__content__actions">
          <cdr-button
            v-for="(cta, i) in computedActions"
            :key="i"
            :href="cta.href"
            :modifier="cta.modifier || 'primary'"
            data-ui="lead-rail-card-component-cta"
            tag="a"
            :class="[
              'lead-rail-card__cta',
              { 'lead-rail-card__cta--dark-secondary': cta.modifier === 'dark-secondary' },
            ]"
          >
            {{ cta.text }}
          </cdr-button>
        </div>
        <cdr-link
          v-if="content.actions.alternateLink"
          :href="content.actions.alternateLink.href"
          data-ui="lead-rail-card-component-alternate-link"
          class="lead-rail-card__alternate-link"
        >
          {{ content.actions.alternateLink.text }}
        </cdr-link>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.lead-rail-card {
  display: grid;
  background: var(--palette-background-color);
  scroll-snap-align: center;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr;
  flex-shrink: 0 !important;
  flex-basis: 100%;
  height: 100%;
  align-items: start;
  width: calc((100vw - 8px) / 1.25);
  max-width: 100%;

  @include cdr-sm-mq-up {
    width: calc((100vw - 8px) / 2.25);
  }

  @include cdr-lg-mq-up {
      width: calc((100vw - 8px) / 3.25);
  }

  /**
  * NOTE: if the aspect ratio here changes,
  * change it for the '.control-height' div in the rail too!
  */
  &__hero {
    position: relative;
    display: flex;
    align-items: flex-end;
    width: 100%;
    height: auto;
    vertical-align: middle;
    overflow: hidden;
    aspect-ratio: 320/320;

    :deep(.landing-image-component) {
      object-fit: cover;
      width: 100%;
      height: 100%;
      position: absolute;
    }

    :deep(.landing-image-component__image) {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }

    .lead-rail-card__badge{
      position: absolute;
      top: $cdr-space-two-x;
      left: 0;
    }

    &__heading {
      width: 100%;
      padding: $cdr-space-one-x;
      padding-bottom: var(--cdr-type-scale-0);
      z-index: 1;

      :deep(.landing-image-component) {
        position: relative;
        display: inline-block;
        width: auto;
      }
    }

    &__title {
      font-size: var(--cdr-type-scale-1);
      line-height: 1.25em;
      color: $cdr-color-text-inverse;
      margin: 0;
      padding: 0;
    }

    &::before {
      background: var(--efectGradient);
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
    }
  }

  &__content {
    width: 100%;
    padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-one-and-a-half-x;
    align-self: start;
    background-color: var(--palette-background-color);
    display: flex;
    flex-direction: column;
    height: 100%;

    @include cdr-sm-mq-up {
      padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-one-and-a-half-x;
    }

    &__body {
      color: var(--palette-body-color);
      display: inline-flex;
      flex-grow:1;
    }

    &__actions-container {
      display: flex;
      margin-top: $cdr-space-one-x;
      justify-content: space-between;
      align-items: flex-end;
      gap: $cdr-space-half-x;
      flex-shrink: 0;
    }

    &__actions{
      display: flex;
      gap: $cdr-space-half-x;
      flex-shrink: 0;
    }

    :deep(.ls-content-block__alternate-link){
      flex-shrink: 0;
      color: var(--palette-alt-link-color);
    }

    :deep(.ls-content-block__region--bottom){
      flex-direction: row;
      align-items: flex-end;
    }

    &__actions{
      display: flex;
      gap: $cdr-space-half-x;
    }

    :deep(.ls-content-block__alternate-link){
      flex-shrink: 0;
    }
  }

  &__alternate-link {
    color: var(--palette-alt-link-color);
  }

  &__cta {
    &--dark-secondary {
      box-shadow: inset 0 0 0 .1rem
        var(--palette-accent-text-color, var(--palette-accent-text-color, #958e83));
      color: var(--palette-accent-text-color);
      &:hover {
        box-shadow: inset 0 0 0 .3rem
          var(--palette-accent-text-color, var(--palette-accent-text-color, #958e83));
      }
    }
  }
}
</style>
/* v8 ignore stop */
